import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Button,
  Checkbox,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AdditionalContactField from "../../../../../../../components/additional-contact/additional-contact";
import DialogContainer from "../../../../../../../components/dialog-container/dialog-container";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientAllUsersRequest,
  getSubdivisionDetailsRequest,
} from "../../../../../../../redux/clients/slice";
import Loading from "../../../../../../../components/loading/loading";
import ErrorNotification from "../../../../../../../components/error-notification/error-notification";
import { updateClientDivisionContactsRequest } from "../../../../../../../utils/APICalls";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
  clientId: string;
};

export default function EditContactDetails({
  showDialog,
  closeDialog,
  data,
  clientId,
}: props) {
  const [useParentPrimaryContact, setUseParentPrimaryContact] = useState(false);
  const [selectedManager, setSelectedManager] = useState("contact0");
  const [divisionManagers, setDivisionManagers] = useState<any[]>([]);
  const [isDivisionManagerEmptyError, setIsDivisionManagerEmptyError] =
    useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();
  const { loading, clientAllUsers } = useSelector(
    (state: any) => state?.clients
  );

  useEffect(() => {
    dispatch(getClientAllUsersRequest({ id: clientId }));
  }, [dispatch]); // eslint-disable-line

  useEffect(() => {
    if (data) {
      setUseParentPrimaryContact(data.useParentPrimaryContact);
      if (!data.useParentPrimaryContact) {
        const allDivisionManagers = [];
        const primaryManager = data?.divisionManagers?.filter(
          (e: any) => e.isPrimary
        );
        if (primaryManager.length > 0) {
          allDivisionManagers.push(primaryManager[0]);
        }
        const nonPrimaryManager = data?.divisionManagers?.filter(
          (e: any) => e.isPrimary === false
        );
        if (nonPrimaryManager.length > 0) {
          allDivisionManagers.push(...nonPrimaryManager);
        }
        if (allDivisionManagers.length === 0) {
          //if there is no user then add a user field with primary
          allDivisionManagers.push({ userId: "", isPrimary: true });
        }
        setDivisionManagers(allDivisionManagers);
      } else {
        setDivisionManagers([{ userId: "", isPrimary: true }]);
      }
    }
  }, [data]);

  const handleSelectedDivisionManagerChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedManager(event.target.value);
    const allDivisionManagers = JSON.parse(JSON.stringify(divisionManagers));
    if (selectedIndex >= 0) {
      // if radio selected set to primary
      allDivisionManagers.forEach(
        (allRelationshipManager: any, index: number) => {
          if (index === selectedIndex) {
            allRelationshipManager.isPrimary = true;
          } else {
            allRelationshipManager.isPrimary = false;
          }
        }
      );
    }
    setDivisionManagers(allDivisionManagers);
  };

  const onDivisionManagersChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const allDivisionManagers = JSON.parse(JSON.stringify(divisionManagers));
    allDivisionManagers[index].userId = e.target.value;
    setDivisionManagers(allDivisionManagers);
  };

  const addDivisionManagersClick = () => {
    setDivisionManagers((prev) => [...prev, { userId: "", isPrimary: false }]);
  };

  const handleDivisionManagerDelete = (index: number) => {
    const allDivisionManagers = [...divisionManagers];
    allDivisionManagers.splice(index, 1);
    setDivisionManagers(allDivisionManagers);

    //if delete user is above selected user then reset user by -1
    const selectedIndex = Number(
      selectedManager.substring(selectedManager.length - 1)
    );
    if (selectedIndex > index)
      setSelectedManager(`contact${selectedIndex - 1}`);
  };

  const onSubmitClick = () => {
    if (isDivisionManagerEmpty()) {
      setIsDivisionManagerEmptyError(true);
      return;
    }

    if (isDivisionManagerDuplicate()) {
      return;
    }

    const allDivisionManagers: any[] = [];
    divisionManagers.forEach((divisionManager) => {
      const manager = {
        userId: divisionManager.userId,
        isPrimary: divisionManager.isPrimary,
      };
      allDivisionManagers.push(manager);
    });

    const payload = {
      useParentPrimaryContact,
      divisionManagers: allDivisionManagers,
    };
    updateDivisionContacts(payload);
  };

  const isDivisionManagerEmpty = () => {
    let isEmpty = false;
    divisionManagers.forEach((divisionManager) => {
      if (!divisionManager.userId) isEmpty = true;
    });
    return isEmpty;
  };

  const isDivisionManagerDuplicate = () => {
    let isDuplicate = false;
    const valueArr = divisionManagers.map((item) => item.userId);
    isDuplicate = valueArr.some((item, idx) => valueArr.indexOf(item) !== idx);
    return isDuplicate;
  };

  const updateDivisionContacts = async (payload: any) => {
    setShowLoading(true);
    const response = await updateClientDivisionContactsRequest(
      clientId,
      data?.id,
      payload
    );
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(
        getSubdivisionDetailsRequest({ id: clientId, subDivisionId: data?.id })
      );
      closeDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <DialogContainer
      title="Edit Client Division - Contact Details"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={onSubmitClick}
    >
      <Box>
        <Box className="user-name-layout">
          <Typography className="label-txt">Division Contact</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControlLabel
              sx={{ mt: -1 }}
              control={
                <Checkbox
                  checked={useParentPrimaryContact}
                  onChange={(event) =>
                    setUseParentPrimaryContact(event.target.checked)
                  }
                />
              }
              label="Same as Main Account Manager"
            />
            {!useParentPrimaryContact && (
              <>
                <Typography fontSize={14}>Primary</Typography>
                {divisionManagers.map((item, index) => (
                  <AdditionalContactField
                    index={index}
                    key={index}
                    selectedValue={selectedManager}
                    onRadioChange={(e: any) =>
                      handleSelectedDivisionManagerChange(e, index)
                    }
                    value={item.userId}
                    onValueChange={(e: any) =>
                      onDivisionManagersChange(e, index)
                    }
                    onDelete={() => handleDivisionManagerDelete(index)}
                    allUsers={clientAllUsers}
                    selectedUsers={divisionManagers}
                    isUserEmptyError={isDivisionManagerEmptyError}
                  />
                ))}
                {divisionManagers?.length < 4 && (
                  <Button
                    sx={{ mt: 2, fontWeight: "510" }}
                    variant="text"
                    startIcon={<AddOutlinedIcon />}
                    onClick={addDivisionManagersClick}
                  >
                    Add Additional
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
      {(loading || showLoading) && <Loading />}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
